import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Copy,
  DollarSign,
  PhoneCall,
  Radio,
  Sparkles,
  Target,
  Upload,
  Users,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, ErrorState, LoadingState } from '@/components/ui/primitives';
import { useAuth } from '@/lib/auth';
import { useIsEnlarged } from '@/lib/useUiScale';
import { cn, formatCents, formatDateTime, pluralize } from '@/lib/utils';

export function DashboardPage() {
  const { authEnabled, user } = useAuth();
  const isVa = authEnabled && user?.role === 'va';
  return isVa ? <VaDashboard /> : <AdminDashboard />;
}

function AdminDashboard() {
  const dashboard = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard, refetchInterval: 30_000 });
  const followUps = useQuery({ queryKey: ['follow-ups'], queryFn: api.followUps });
  const jobs = useQuery({ queryKey: ['job-status'], queryFn: api.jobStatus });
  const enlarged = useIsEnlarged();

  if (dashboard.isLoading) return <Page title="Home"><div className="page-stack"><LoadingState /></div></Page>;
  if (dashboard.error) return <Page title="Home"><ErrorState error={dashboard.error} onRetry={() => dashboard.refetch()} /></Page>;

  const m = dashboard.data!;
  const overdue = followUps.data?.counts.overdue ?? 0;
  const dueToday = followUps.data?.counts.dueToday ?? 0;

  return (
    <Page
      title="Home"
      description={`${m.totalActiveLeads.toLocaleString()} active ${pluralize(m.totalActiveLeads, 'lead')} · your CRM at a glance`}
      actions={<Button asChild><Link to="/queue"><PhoneCall className="h-4 w-4" />Start calling</Link></Button>}
    >
      <div className="page-stack">
        <PhoneLineWarnings />

        <div className={cn('grid gap-3', enlarged ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-4')}>
          <Metric icon={Users} label="Active leads" value={m.totalActiveLeads.toLocaleString()} hint={`${m.newLeadsToday} new today`} />
          <Metric icon={PhoneCall} label="Calls today" value={m.callsToday.toLocaleString()} hint="across all callers" tone="blue" />
          <Metric icon={Target} label="Follow-ups due" value={m.followUpsDue.toLocaleString()} hint={overdue ? `${overdue} overdue` : 'nothing overdue'} tone={overdue ? 'amber' : 'green'} />
          <Metric icon={Sparkles} label="Interested" value={m.interestedLeads.toLocaleString()} hint="active opportunities" tone="green" />
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.25fr_0.75fr]">
          <Card>
            <CardHeader>
              <div><CardTitle>Revenue snapshot</CardTitle><p className="mt-0.5 text-2xs text-muted-foreground">Sales and payments currently recorded in LeadForge</p></div>
              <DollarSign className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardBody className="grid gap-3 md:grid-cols-3">
              <MoneyMetric label="Booked sales" value={formatCents(m.bookedSalesCents)} />
              <MoneyMetric label="Collected" value={formatCents(m.collectedPaymentsCents)} tone="green" />
              <MoneyMetric label="Outstanding" value={formatCents(m.outstandingBalanceCents)} tone={m.outstandingBalanceCents > 0 ? 'amber' : undefined} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader><CardTitle>Needs attention</CardTitle><Badge variant={overdue || m.importFailures ? 'warning' : 'success'}>{overdue + m.importFailures + m.duplicateCandidatesPending}</Badge></CardHeader>
            <CardBody className="space-y-1.5">
              <AttentionRow icon={PhoneCall} label="Overdue follow-ups" count={overdue} to="/follow-ups" tone={overdue ? 'warn' : undefined} />
              <AttentionRow icon={Copy} label="Duplicate candidates" count={m.duplicateCandidatesPending} to="/duplicates" />
              <AttentionRow icon={AlertTriangle} label="Failed imports" count={m.importFailures} to="/imports" tone={m.importFailures ? 'warn' : undefined} />
              <AttentionRow icon={Upload} label="Imported today" count={m.leadsImportedToday} to="/imports" />
            </CardBody>
          </Card>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Upcoming follow-ups</CardTitle><Button asChild variant="ghost" size="xs"><Link to="/follow-ups">View all<ArrowRight className="h-3 w-3" /></Link></Button></CardHeader>
            <CardBody className="space-y-2">
              {[...(followUps.data?.overdue ?? []), ...(followUps.data?.dueToday ?? []), ...(followUps.data?.upcoming ?? [])].slice(0, 5).map((item) => (
                <Link key={item.id} to={`/queue/lead-${item.leadId}`} className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-accent">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><PhoneCall className="h-3.5 w-3.5" /></div>
                  <div className="min-w-0 flex-1"><p className="truncate text-xs font-bold">{item.leadBusinessName}</p><p className="text-2xs text-muted-foreground">{item.followUpType} · {item.snoozedUntilDate ?? item.dueDate}</p></div>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                </Link>
              ))}
              {!followUps.data || (followUps.data.overdue.length + followUps.data.dueToday.length + followUps.data.upcoming.length === 0) ? <p className="py-4 text-center text-xs text-muted-foreground">No follow-ups on deck.</p> : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader><CardTitle>Background work</CardTitle><CheckCircle2 className="h-4 w-4 text-emerald-500" /></CardHeader>
            <CardBody className="space-y-3 text-xs">
              <JobRow label="Lead scoring" job={jobs.data?.scoring} />
              <JobRow label="Website summaries" job={jobs.data?.summaries} />
              <div className="flex items-center justify-between border-t border-border/70 pt-3"><span className="text-muted-foreground">Watched import folder</span><Badge variant={jobs.data?.watchedFolder.running ? 'success' : 'default'}>{jobs.data?.watchedFolder.running ? 'Watching' : 'Off'}</Badge></div>
            </CardBody>
          </Card>
        </div>
      </div>
    </Page>
  );
}

function VaDashboard() {
  const navigate = useNavigate();
  const metrics = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard, refetchInterval: 30_000 });
  const sessions = useQuery({ queryKey: ['call-sessions'], queryFn: () => api.callSessions(false) });
  const followUps = useQuery({ queryKey: ['follow-ups'], queryFn: api.followUps });

  if (metrics.isLoading || sessions.isLoading) return <Page title="Home"><div className="page-stack"><LoadingState /></div></Page>;
  if (metrics.error) return <Page title="Home"><ErrorState error={metrics.error} onRetry={() => metrics.refetch()} /></Page>;

  const m = metrics.data!;
  const current = sessions.data?.[0] ?? null;
  const remaining = (sessions.data ?? []).reduce((sum, session) => sum + session.pending, 0);
  const overdue = followUps.data?.counts.overdue ?? 0;
  const due = [
    ...(followUps.data?.overdue ?? []).map((item) => ({ item, overdue: true })),
    ...(followUps.data?.dueToday ?? []).map((item) => ({ item, overdue: false })),
    ...(followUps.data?.upcoming ?? []).map((item) => ({ item, overdue: false })),
  ].slice(0, 5);
  const worked = current ? current.consumed + current.skipped + current.removed : 0;
  const total = current?.session.totalItems ?? 0;
  const percent = total ? Math.round((worked / total) * 100) : 0;

  return (
    <Page
      title="Home"
      description="Your assigned work, queue progress, and follow-ups"
      actions={<Button onClick={() => current?.pending ? navigate(`/queue/${current.session.id}`) : navigate('/queue')}><PhoneCall className="h-4 w-4" />{current?.pending ? 'Resume queue' : 'Go to My Work'}</Button>}
    >
      <div className="page-stack">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <Metric icon={Users} label="Assigned to me" value={m.totalActiveLeads.toLocaleString()} hint={`${m.newLeadsToday} new today`} />
          <Metric icon={PhoneCall} label="Calls today" value={m.callsToday.toLocaleString()} hint="your activity" tone="blue" />
          <Metric icon={Target} label="Follow-ups due" value={m.followUpsDue.toLocaleString()} hint={overdue ? `${overdue} overdue` : 'on track'} tone={overdue ? 'amber' : 'green'} />
          <Metric icon={Sparkles} label="Left in queue" value={remaining.toLocaleString()} hint={remaining ? 'ready to work' : 'no active queue'} tone={remaining ? 'green' : undefined} />
        </div>

        <Card>
          <CardHeader><div><CardTitle>My queue progress</CardTitle><p className="mt-0.5 text-2xs text-muted-foreground">{current ? current.session.name : 'No active calling session'}</p></div>{current?.pending ? <Button size="sm" onClick={() => navigate(`/queue/${current.session.id}`)}>Resume queue<ArrowRight className="h-3.5 w-3.5" /></Button> : <Button asChild size="sm"><Link to="/queue">Open My Work</Link></Button>}</CardHeader>
          <CardBody>
            <div className="flex items-end justify-between gap-3"><div><p className="text-sm font-bold">{worked.toLocaleString()} of {total.toLocaleString()} worked</p><p className="mt-1 text-xs text-muted-foreground">{current ? `${current.consumed} called · ${current.skipped} skipped · ${current.removed} removed` : 'Start a queue when you are ready to call.'}</p></div><span className="text-sm font-extrabold text-primary">{percent}%</span></div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} /></div>
          </CardBody>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Upcoming follow-ups</CardTitle><Button asChild variant="ghost" size="xs"><Link to="/follow-ups">View all<ArrowRight className="h-3 w-3" /></Link></Button></CardHeader>
            <CardBody className="space-y-2">
              {due.map(({ item, overdue: isOverdue }) => <Link key={item.id} to={`/queue/lead-${item.leadId}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-accent"><div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', isOverdue ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary')}><PhoneCall className="h-3.5 w-3.5" /></div><div className="min-w-0 flex-1"><p className="truncate text-xs font-bold">{item.leadBusinessName}</p><p className="text-2xs text-muted-foreground">{item.followUpType} · {item.snoozedUntilDate ?? item.dueDate}</p></div>{isOverdue && <Badge variant="danger">Overdue</Badge>}</Link>)}
              {!due.length && <p className="py-5 text-center text-xs text-muted-foreground">Nothing due right now.</p>}
            </CardBody>
          </Card>
          <Card>
            <CardHeader><CardTitle>Quick start</CardTitle><Sparkles className="h-4 w-4 text-primary" /></CardHeader>
            <CardBody className="space-y-2">
              <QuickAction title="Work my queue" body="Continue calling assigned leads in order." to="/queue" />
              <QuickAction title="Review my leads" body="See every lead currently assigned to you." to="/leads" />
              <QuickAction title="Handle follow-ups" body="Work overdue and scheduled callbacks." to="/follow-ups" />
            </CardBody>
          </Card>
        </div>
      </div>
    </Page>
  );
}

function Metric({ icon: Icon, label, value, hint, tone }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; hint: string; tone?: 'blue' | 'green' | 'amber' }) {
  const toneClass = tone === 'green' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300' : tone === 'amber' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-300' : 'bg-primary/10 text-primary';
  return <div className="surface-panel p-4"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="text-xs font-medium text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-extrabold tracking-tight tabular-nums">{value}</p><p className={cn('mt-1 text-2xs font-semibold', tone === 'amber' ? 'text-amber-600 dark:text-amber-300' : tone === 'green' ? 'text-emerald-600 dark:text-emerald-300' : 'text-muted-foreground')}>{hint}</p></div><div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', toneClass)}><Icon className="h-4 w-4" /></div></div></div>;
}

function MoneyMetric({ label, value, tone }: { label: string; value: string; tone?: 'green' | 'amber' }) {
  return <div className="rounded-xl bg-muted/55 p-3.5"><p className="text-xs font-medium text-muted-foreground">{label}</p><p className={cn('mt-1 text-xl font-extrabold tracking-tight', tone === 'green' && 'text-emerald-600 dark:text-emerald-300', tone === 'amber' && 'text-amber-600 dark:text-amber-300')}>{value}</p></div>;
}

function AttentionRow({ icon: Icon, label, count, to, tone }: { icon: React.ComponentType<{ className?: string }>; label: string; count: number; to: string; tone?: 'warn' }) {
  return <Link to={to} className="group flex items-center gap-2.5 rounded-lg px-2 py-2 text-xs transition-colors hover:bg-accent"><Icon className={cn('h-3.5 w-3.5 shrink-0', tone === 'warn' && count > 0 ? 'text-amber-600 dark:text-amber-300' : 'text-muted-foreground')} /><span className="flex-1 truncate text-muted-foreground group-hover:text-foreground">{label}</span><span className={cn('font-bold tabular-nums', tone === 'warn' && count > 0 && 'text-amber-600 dark:text-amber-300')}>{count.toLocaleString()}</span><ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" /></Link>;
}

function QuickAction({ title, body, to }: { title: string; body: string; to: string }) {
  return <Link to={to} className="group flex items-center gap-3 rounded-xl border border-border/70 bg-muted/25 p-3 transition-all hover:border-primary/20 hover:bg-accent"><div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><ArrowRight className="h-3.5 w-3.5" /></div><div className="min-w-0 flex-1"><p className="text-xs font-bold">{title}</p><p className="mt-0.5 text-2xs text-muted-foreground">{body}</p></div></Link>;
}

function JobRow({ label, job }: { label: string; job: { status: string; processedItems: number; totalItems: number; message: string | null } | null | undefined }) {
  if (!job) return <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><Badge>Never run</Badge></div>;
  const pct = job.totalItems ? Math.round((job.processedItems / job.totalItems) * 100) : 0;
  const variant = job.status === 'completed' ? 'success' : job.status === 'failed' ? 'danger' : job.status === 'running' ? 'primary' : 'default';
  return <div><div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><Badge variant={variant}>{job.status}</Badge></div>{job.status === 'running' && <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>}{job.message && <p className="mt-1 truncate text-2xs text-muted-foreground">{job.message}</p>}</div>;
}

function PhoneLineWarnings(): JSX.Element | null {
  const { authEnabled, user } = useAuth();
  const isVa = authEnabled && user?.role === 'va';
  const warnings = useQuery({ queryKey: ['phone-line-warnings'], queryFn: api.phoneLineWarnings, enabled: !isVa, retry: false });
  const list = warnings.data?.warnings ?? [];
  if (isVa || !list.length) return null;
  return <Card className="border-amber-500/20 bg-amber-500/5"><CardHeader><CardTitle className="flex items-center gap-2"><Radio className="h-4 w-4 text-amber-600 dark:text-amber-300" />Phone lines need attention</CardTitle><Button asChild variant="outline" size="sm"><Link to="/phone-lines">Manage<ArrowRight className="h-3.5 w-3.5" /></Link></Button></CardHeader><CardBody className="grid gap-2 md:grid-cols-2">{list.slice(0, 4).map((warning, index) => <div key={`${warning.kind}-${index}`} className="flex items-start gap-2 rounded-lg bg-card/70 p-2.5 text-xs"><AlertTriangle className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', warning.severity === 'danger' ? 'text-destructive' : 'text-amber-600 dark:text-amber-300')} /><span>{warning.message}</span></div>)}</CardBody></Card>;
}
